import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Type, Rocket, RotateCcw, X, Sparkles, Loader2, ChevronRight,
  Bold, Italic, Underline, CornerDownLeft, Languages, AlertCircle,
  CheckCircle2, FileText
} from "lucide-react";
import { api } from "../../lib/api";
import { useI18n } from "../../i18n";
import { useTexts } from "../../lib/texts";

/**
 * CMS text editor. Same layout language as ImagesEditor:
 *   sidebar of pages -> cards of text slots -> per-language rich editor.
 *
 * Each slot exposes three tabs (ES / EN / PT). Content is edited via a
 * contentEditable div with a minimal toolbar (bold / italic / underline /
 * line break). Values are stored & rendered as light HTML.
 */
export default function TextsEditor() {
  const { pick, lang } = useI18n();
  const { reload: reloadPublic } = useTexts();

  const [pages, setPages] = useState([]);
  const [slots, setSlots] = useState([]);
  const [activePage, setActivePage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/texts");
      setPages(r.data?.pages || []);
      setSlots(r.data?.slots || []);
      if (!activePage && r.data?.pages?.length) setActivePage(r.data.pages[0].key);
    } catch {
      toast.error(pick("No se pudieron cargar los textos", "Failed to load texts", "Falha ao carregar os textos"));
    } finally {
      setLoading(false);
    }
  }, [activePage, pick]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pageSlots = useMemo(() => slots.filter((s) => s.page === activePage), [slots, activePage]);
  const totalDrafts = useMemo(() => slots.filter((s) => s.has_draft).length, [slots]);
  const pageDrafts = useMemo(() => pageSlots.filter((s) => s.has_draft).length, [pageSlots]);

  const saveDraft = async (slotKey, draft) => {
    setBusyKey(slotKey);
    try {
      const r = await api.post(`/admin/texts/${slotKey}/draft`, draft);
      setSlots((prev) => prev.map((s) => (s.key === slotKey ? r.data.slot : s)));
      toast.success(pick("Borrador guardado", "Draft saved", "Rascunho salvo"));
    } catch (e) {
      toast.error(e.response?.data?.detail || pick("Error al guardar", "Save failed", "Falha ao salvar"));
    } finally {
      setBusyKey(null);
    }
  };

  const publish = async (slotKey) => {
    setBusyKey(slotKey);
    try {
      const r = await api.post(`/admin/texts/${slotKey}/publish`);
      setSlots((prev) => prev.map((s) => (s.key === slotKey ? r.data.slot : s)));
      reloadPublic();
      toast.success(pick("Publicado en el sitio", "Published live", "Publicado no site"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setBusyKey(null);
    }
  };

  const publishAll = async () => {
    if (!totalDrafts) return;
    setBusyKey("__all__");
    try {
      await api.post("/admin/texts/publish-all");
      await load();
      reloadPublic();
      toast.success(pick(`Publicados ${totalDrafts} cambios`, `Published ${totalDrafts} changes`, `Publicados ${totalDrafts} alterações`));
    } catch {
      toast.error("Error");
    } finally {
      setBusyKey(null);
    }
  };

  const discard = async (slotKey) => {
    setBusyKey(slotKey);
    try {
      const r = await api.post(`/admin/texts/${slotKey}/discard`);
      setSlots((prev) => prev.map((s) => (s.key === slotKey ? r.data.slot : s)));
      toast.success(pick("Borrador descartado", "Draft discarded", "Rascunho descartado"));
    } catch {
      toast.error("Error");
    } finally {
      setBusyKey(null);
    }
  };

  const reset = async (slotKey) => {
    if (!window.confirm(pick("¿Restaurar texto original?", "Reset to original text?", "Restaurar texto original?"))) return;
    setBusyKey(slotKey);
    try {
      const r = await api.post(`/admin/texts/${slotKey}/reset`);
      setSlots((prev) => prev.map((s) => (s.key === slotKey ? r.data.slot : s)));
      reloadPublic();
      toast.success(pick("Restaurado", "Reset done", "Restaurado"));
    } catch {
      toast.error("Error");
    } finally {
      setBusyKey(null);
    }
  };

  const activePageMeta = pages.find((p) => p.key === activePage);

  return (
    <div className="text-white" data-testid="admin-texts-editor">
      {/* Header */}
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-400/30 bg-cyan-400/10 mb-3">
            <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
            <span className="label-mono !text-cyan-300 text-[10px]">
              {pick("EDITOR DE CONTENIDO", "CONTENT EDITOR", "EDITOR DE CONTEÚDO")}
            </span>
          </div>
          <h1 className="font-display text-3xl md:text-4xl chrome-text leading-tight">
            {pick("Editor de textos", "Text editor", "Editor de textos")}
          </h1>
          <p className="text-white/60 text-sm mt-2 max-w-xl">
            {pick(
              "Edita títulos, subtítulos y textos clave de cada página en los 3 idiomas (ES · EN · PT). Los cambios se guardan como borrador hasta que los publiques.",
              "Edit titles, subtitles and key texts of each page in all 3 languages (ES · EN · PT). Changes are saved as drafts until you publish.",
              "Edite títulos, subtítulos e textos-chave de cada página em 3 idiomas (ES · EN · PT). As alterações ficam como rascunho até você publicar."
            )}
          </p>
        </div>
        {totalDrafts > 0 && (
          <button
            onClick={publishAll}
            disabled={busyKey === "__all__"}
            data-testid="publish-all-texts-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400 text-black font-tech uppercase tracking-wider text-xs shadow-[0_0_28px_-4px_rgba(0,240,255,0.7)] hover:shadow-[0_0_36px_-4px_rgba(0,240,255,0.95)] transition disabled:opacity-60">
            {busyKey === "__all__" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
            {pick(`Publicar ${totalDrafts} cambios`, `Publish ${totalDrafts} changes`, `Publicar ${totalDrafts} alterações`)}
          </button>
        )}
      </div>

      {loading ? (
        <div className="py-24 text-center text-cyan-300 label-mono">
          <Loader2 className="w-6 h-6 animate-spin inline-block" />
        </div>
      ) : (
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Sidebar */}
          <aside className="lg:col-span-3">
            <div className="glass-card chrome-border p-3 sticky top-8">
              <div className="label-mono text-[10px] px-2 pt-1 pb-3">{pick("PÁGINAS", "PAGES", "PÁGINAS")}</div>
              <div className="space-y-1">
                {pages.map((p) => {
                  const draftCount = slots.filter((s) => s.page === p.key && s.has_draft).length;
                  const total = slots.filter((s) => s.page === p.key).length;
                  const active = activePage === p.key;
                  return (
                    <button
                      key={p.key}
                      onClick={() => setActivePage(p.key)}
                      data-testid={`text-page-tab-${p.key}`}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg transition text-left ${
                        active
                          ? "bg-cyan-400/10 border border-cyan-400/30 text-cyan-300"
                          : "border border-transparent text-white/70 hover:bg-white/5 hover:text-white"
                      }`}>
                      <div className="min-w-0">
                        <div className="font-tech text-sm truncate">{p[`label_${lang}`] || p.label_es}</div>
                        <div className="text-[10px] text-white/40 mt-0.5">{total} {pick("textos", "texts", "textos")}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        {draftCount > 0 && (
                          <span className="text-[9px] font-tech uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-400/20 border border-amber-400/40 text-amber-200">
                            {draftCount}
                          </span>
                        )}
                        <ChevronRight className={`w-3.5 h-3.5 transition ${active ? "opacity-100" : "opacity-30"}`} />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          {/* Slots */}
          <div className="lg:col-span-9">
            <div className="mb-4 flex items-baseline justify-between">
              <div>
                <div className="label-mono text-[10px]">/ {pick("SECCIÓN", "SECTION", "SEÇÃO")}</div>
                <h2 className="font-display text-2xl md:text-3xl chrome-text mt-1">
                  {activePageMeta ? activePageMeta[`label_${lang}`] || activePageMeta.label_es : ""}
                </h2>
              </div>
              {pageDrafts > 0 && (
                <div className="text-xs text-amber-200 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {pageDrafts} {pick("con borrador", "with draft", "com rascunho")}
                </div>
              )}
            </div>

            <div className="space-y-5">
              {pageSlots.map((s) => (
                <SlotEditor
                  key={s.key}
                  slot={s}
                  busy={busyKey === s.key}
                  lang={lang}
                  pick={pick}
                  onSaveDraft={(draft) => saveDraft(s.key, draft)}
                  onPublish={() => publish(s.key)}
                  onDiscard={() => discard(s.key)}
                  onReset={() => reset(s.key)}
                />
              ))}
              {pageSlots.length === 0 && (
                <div className="py-12 text-center text-white/50 label-mono">
                  {pick("Sin textos editables en esta sección", "No editable texts in this section", "Sem textos editáveis nesta seção")}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ============================== SlotEditor ============================== */
const LANG_META = [
  { code: "es", flag: "🇪🇸", label: "Español" },
  { code: "en", flag: "🇺🇸", label: "English" },
  { code: "pt", flag: "🇧🇷", label: "Português" },
];

function SlotEditor({ slot, busy, lang, pick, onSaveDraft, onPublish, onDiscard, onReset }) {
  const [activeLang, setActiveLang] = useState(lang || "es");
  const [values, setValues] = useState({
    es: slot.draft?.es || slot.published?.es || slot.default?.es || "",
    en: slot.draft?.en || slot.published?.en || slot.default?.en || "",
    pt: slot.draft?.pt || slot.published?.pt || slot.default?.pt || "",
  });
  const [translating, setTranslating] = useState(null);

  // Re-sync when parent slot changes (after publish/discard/reset)
  useEffect(() => {
    setValues({
      es: slot.draft?.es || slot.published?.es || slot.default?.es || "",
      en: slot.draft?.en || slot.published?.en || slot.default?.en || "",
      pt: slot.draft?.pt || slot.published?.pt || slot.default?.pt || "",
    });
  }, [slot.key, slot.updated_at, slot.has_draft, slot.is_overridden]);

  const status = slot.has_draft ? "draft" : slot.is_overridden ? "published" : "default";
  const label = slot.label?.[lang] || slot.label?.es;
  const description = slot.description?.[lang] || slot.description?.es;

  const isDirty = LANG_META.some(({ code }) => {
    const currentPublished = slot.published?.[code] || slot.default?.[code] || "";
    return values[code] !== currentPublished;
  });

  const save = () => {
    // Only send languages that differ from published/default
    const draft = {};
    for (const { code } of LANG_META) {
      const publishedOrDefault = slot.published?.[code] || slot.default?.[code] || "";
      if (values[code] !== publishedOrDefault) draft[code] = values[code];
    }
    if (!Object.keys(draft).length) return;
    onSaveDraft(draft);
  };

  const translateFrom = async (source, target) => {
    const text = values[source];
    if (!text?.trim()) {
      toast.error(pick("Texto vacío", "Empty text", "Texto vazio"));
      return;
    }
    setTranslating(target);
    try {
      const { data } = await api.post("/admin/translate", { text, target });
      setValues((v) => ({ ...v, [target]: data.translated }));
      toast.success(pick("Traducción aplicada", "Translation applied", "Tradução aplicada"));
    } catch (e) {
      const msg = e.response?.data?.detail || "";
      if (/budget|balance|exceed/i.test(msg)) {
        toast.error(pick(
          "Saldo Emergent LLM agotado. Recarga en Perfil > Universal Key.",
          "Emergent LLM balance exhausted. Recharge in Profile > Universal Key.",
          "Saldo Emergent LLM esgotado. Recarregue em Perfil > Universal Key."
        ), { duration: 6000 });
      } else {
        toast.error(msg || pick("Error en traducción", "Translation error", "Erro na tradução"));
      }
    } finally {
      setTranslating(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      className="glass-card chrome-border p-5"
      data-testid={`text-slot-${slot.key}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <StatusBadge status={status} pick={pick} />
            <KindBadge kind={slot.kind} pick={pick} />
          </div>
          <h3 className="font-tech text-base text-white leading-tight">{label}</h3>
          <p className="text-xs text-white/55 leading-relaxed mt-1">{description}</p>
          <div className="mt-1 text-[10px] text-white/35 font-tech tracking-wider uppercase">{slot.key}</div>
        </div>
      </div>

      {/* Language tabs */}
      <div className="flex gap-1 mb-3 border-b border-white/10">
        {LANG_META.map(({ code, flag, label: lbl }) => (
          <button
            key={code}
            onClick={() => setActiveLang(code)}
            data-testid={`text-lang-${slot.key}-${code}`}
            className={`px-3 py-2 -mb-px border-b-2 font-tech text-xs uppercase tracking-wider transition ${
              activeLang === code
                ? "border-cyan-400 text-cyan-300"
                : "border-transparent text-white/50 hover:text-white/80"
            }`}>
            <span className="mr-1.5">{flag}</span> {lbl}
          </button>
        ))}
      </div>

      {/* Rich text editor */}
      <RichTextEditor
        key={`${slot.key}-${activeLang}`}
        html={values[activeLang]}
        kind={slot.kind}
        onChange={(html) => setValues((v) => ({ ...v, [activeLang]: html }))}
        placeholder={slot.default?.[activeLang]}
        testId={`text-editor-${slot.key}-${activeLang}`}
      />

      {/* Language actions (translate) */}
      <div className="mt-3 flex flex-wrap gap-2">
        {LANG_META.filter((l) => l.code !== activeLang).map(({ code, flag }) => (
          <button
            key={code}
            onClick={() => translateFrom(activeLang, code)}
            disabled={translating === code || !values[activeLang]}
            data-testid={`translate-${slot.key}-to-${code}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 text-white/70 hover:border-cyan-400/40 hover:text-cyan-200 font-tech text-[10px] uppercase tracking-wider transition disabled:opacity-40">
            {translating === code ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />}
            {pick(`Traducir a`, `Translate to`, `Traduzir para`)} <span>{flag}</span> {code.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Bottom actions */}
      <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-2 items-center justify-between">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={save}
            disabled={busy || !isDirty}
            data-testid={`save-draft-${slot.key}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-cyan-400/40 text-cyan-100 hover:bg-cyan-400/10 font-tech text-[10px] uppercase tracking-wider transition disabled:opacity-40">
            <FileText className="w-3 h-3" /> {pick("Guardar borrador", "Save draft", "Salvar rascunho")}
          </button>
          {slot.has_draft && (
            <>
              <button
                onClick={onPublish}
                disabled={busy}
                data-testid={`publish-text-${slot.key}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400 text-black font-tech text-[10px] uppercase tracking-wider shadow-[0_0_18px_-4px_rgba(0,240,255,0.7)] disabled:opacity-60">
                <Rocket className="w-3 h-3" /> {pick("Publicar", "Publish", "Publicar")}
              </button>
              <button
                onClick={onDiscard}
                disabled={busy}
                data-testid={`discard-text-${slot.key}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/20 text-white/70 hover:border-rose-400/50 hover:text-rose-200 font-tech text-[10px] uppercase tracking-wider transition disabled:opacity-60">
                <X className="w-3 h-3" /> {pick("Descartar borrador", "Discard draft", "Descartar rascunho")}
              </button>
            </>
          )}
        </div>
        {slot.is_overridden && (
          <button
            onClick={onReset}
            disabled={busy}
            data-testid={`reset-text-${slot.key}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/15 text-white/60 hover:border-amber-400/50 hover:text-amber-200 font-tech text-[10px] uppercase tracking-wider transition disabled:opacity-40">
            <RotateCcw className="w-3 h-3" /> {pick("Restaurar original", "Reset to original", "Restaurar original")}
          </button>
        )}
        {busy && <Loader2 className="w-4 h-4 text-cyan-300 animate-spin ml-auto" />}
      </div>
    </motion.div>
  );
}


/* ============================== RichTextEditor ============================== */
function RichTextEditor({ html, kind, onChange, placeholder, testId }) {
  const ref = useRef(null);

  // Set initial content once (uncontrolled contentEditable pattern)
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== html) {
      ref.current.innerHTML = html || "";
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const emit = () => {
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const exec = (cmd) => {
    document.execCommand(cmd, false);
    ref.current?.focus();
    emit();
  };

  const insertBr = () => {
    document.execCommand("insertHTML", false, "<br>");
    emit();
  };

  const onPaste = (e) => {
    // Force plain text on paste — avoids leaking styles from other sites
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
    emit();
  };

  const onKeyDown = (e) => {
    if (kind === "inline" && e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // no newlines in inline slots
    }
  };

  return (
    <div className="border border-white/10 rounded-lg overflow-hidden bg-black/40 focus-within:border-cyan-400/50 transition">
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-white/10 bg-white/[0.02]">
        <ToolBtn onClick={() => exec("bold")} title="Bold (Ctrl+B)"><Bold className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec("italic")} title="Italic (Ctrl+I)"><Italic className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec("underline")} title="Underline (Ctrl+U)"><Underline className="w-3.5 h-3.5" /></ToolBtn>
        <div className="w-px h-4 bg-white/10 mx-1" />
        <ToolBtn onClick={insertBr} title="Line break (Shift+Enter)" disabled={kind === "inline"}>
          <CornerDownLeft className="w-3.5 h-3.5" />
        </ToolBtn>
        <div className="ml-auto text-[10px] label-mono text-white/40">
          {kind === "inline" ? "1 LÍNEA" : "PÁRRAFO"}
        </div>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onPaste={onPaste}
        onKeyDown={onKeyDown}
        data-testid={testId}
        data-placeholder={placeholder || ""}
        className={`px-3 py-2.5 outline-none text-white/90 text-sm leading-relaxed empty:before:content-[attr(data-placeholder)] empty:before:text-white/30 min-h-[${kind === "inline" ? "40px" : "80px"}]`}
        style={{ minHeight: kind === "inline" ? 40 : 80 }}
      />
    </div>
  );
}

function ToolBtn({ onClick, title, disabled, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      disabled={disabled}
      className="w-7 h-7 flex items-center justify-center rounded text-white/70 hover:bg-white/10 hover:text-white transition disabled:opacity-30 disabled:hover:bg-transparent">
      {children}
    </button>
  );
}


/* ============================== Badges ============================== */
function StatusBadge({ status, pick }) {
  if (status === "draft") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400/20 border border-amber-400/50 text-amber-100 font-tech text-[9px] uppercase tracking-wider">
        <AlertCircle className="w-2.5 h-2.5" /> {pick("BORRADOR", "DRAFT", "RASCUNHO")}
      </span>
    );
  }
  if (status === "published") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-400/20 border border-emerald-400/50 text-emerald-100 font-tech text-[9px] uppercase tracking-wider">
        <CheckCircle2 className="w-2.5 h-2.5" /> {pick("PUBLICADO", "LIVE", "PUBLICADO")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/40 border border-white/20 text-white/70 font-tech text-[9px] uppercase tracking-wider">
      <Type className="w-2.5 h-2.5" /> {pick("ORIGINAL", "ORIGINAL", "ORIGINAL")}
    </span>
  );
}

function KindBadge({ kind, pick }) {
  const label = kind === "inline"
    ? pick("TÍTULO / 1 LÍNEA", "HEADING / 1 LINE", "TÍTULO / 1 LINHA")
    : pick("PÁRRAFO", "PARAGRAPH", "PARÁGRAFO");
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-400/10 border border-cyan-400/30 text-cyan-200 font-tech text-[9px] uppercase tracking-wider">
      {label}
    </span>
  );
}
